import {Dialog as DialogPrimitive} from "@base-ui/react/dialog";
import {XIcon} from "lucide-react";
import * as React from "react";

import {cn} from "@/lib/utils";

type DialogDismissConfig = {
  disablePointerDismissal: boolean;
  blockEscapeDismissal: boolean;
};

const EMPTY_DISMISS_CONFIG: DialogDismissConfig = {
  disablePointerDismissal: false,
  blockEscapeDismissal: false,
};

const DialogDismissConfigContext = React.createContext<
  ((config: DialogDismissConfig) => void) | null
>(null);

function Dialog({
  onOpenChange,
  disablePointerDismissal: disablePointerDismissalProp,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const dismissConfigRef = React.useRef(EMPTY_DISMISS_CONFIG);
  const [contentPointerDismissal, setContentPointerDismissal] = React.useState(false);

  const registerDismissConfig = React.useCallback((config: DialogDismissConfig) => {
    dismissConfigRef.current = config;
    setContentPointerDismissal(config.disablePointerDismissal);
  }, []);

  const handleOpenChange = React.useCallback<
    NonNullable<React.ComponentProps<typeof DialogPrimitive.Root>["onOpenChange"]>
  >(
    (open, eventDetails) => {
      if (
        !open &&
        dismissConfigRef.current.blockEscapeDismissal &&
        eventDetails.reason === "escape-key"
      ) {
        eventDetails.cancel();
        return;
      }

      onOpenChange?.(open, eventDetails);
    },
    [onOpenChange],
  );

  return (
    <DialogDismissConfigContext value={registerDismissConfig}>
      <DialogPrimitive.Root
        data-slot="dialog"
        disablePointerDismissal={disablePointerDismissalProp ?? contentPointerDismissal}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogDismissConfigContext>
  );
}

function DialogTrigger({...props}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({...props}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({...props}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({className, ...props}: React.ComponentProps<typeof DialogPrimitive.Backdrop>) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

type LegacyRadixDismissEvent = {preventDefault: () => void};

function DialogContent({
  className,
  overlayClassName,
  children,
  showCloseButton = true,
  onPointerDownOutside,
  onEscapeKeyDown,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Popup> & {
  showCloseButton?: boolean;
  overlayClassName?: string;
  /** @deprecated Radix-only. Maps to Base UI `disablePointerDismissal` on `Dialog`. */
  onPointerDownOutside?: (event: LegacyRadixDismissEvent) => void;
  /** @deprecated Radix-only. Maps to escape cancel handling on `Dialog`. */
  onEscapeKeyDown?: (event: LegacyRadixDismissEvent) => void;
}) {
  const registerDismissConfig = React.use(DialogDismissConfigContext);

  React.useLayoutEffect(() => {
    if (!registerDismissConfig) return;

    registerDismissConfig({
      disablePointerDismissal: Boolean(onPointerDownOutside),
      blockEscapeDismissal: Boolean(onEscapeKeyDown),
    });

    return () => {
      registerDismissConfig(EMPTY_DISMISS_CONFIG);
    };
  }, [onPointerDownOutside, onEscapeKeyDown, registerDismissConfig]);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "bg-background data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className,
          // Keep viewport-centered positioning even when consumers pass `relative` for overlays.
          "fixed top-[50%] left-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-open:bg-accent data-open:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({className, ...props}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({className, ...props}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

function DialogTitle({className, ...props}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
